
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import ChatMessageImageDialog from '@/components/dialogs/qa/chat/ChatMessageImageDialog.vue';
import EscalateDialog from '@/components/dialogs/qa/escalation/EscalateDialog.vue';
import DeleteCommentDialog from '@/components/dialogs/qa/comment/DeleteCommentDialog.vue';
import { TicketInterface, TicketPriorities, TicketWSMessageInterface } from '@/services/QA/ticket/interfaces';
import CommentRepositoryInterface, {
  CommentInterface,
  TicketCommentInterface,
  TicketReplyCommentInterface,
} from '@/services/QA/comment/interfaces';
import FeedbackRepositoryInterface, { FeedbackInterface, FeedbackStatus } from '@/services/QA/feedback/interfaces';
import { UserHasScopes } from '@/views/middleware/Route/Auth';
import { Roles } from '@/services/Roles';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import DeleteNoteDialog from '@/components/dialogs/qa/note/DeleteNoteDialog.vue';
import EditCommentDialog from '@/components/dialogs/qa/comment/EditCommentDialog.vue';
import EditNoteDialog from '@/components/dialogs/qa/note/EditNoteDialog.vue';
import { NoteInterface } from '@/services/QA/note/interfaces';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    ChatMessageImageDialog,
    EscalateDialog,
    DeleteCommentDialog,
    DeleteNoteDialog,
    EditCommentDialog,
    EditNoteDialog,
  },
  inject: [
    'feedbackRepository',
    'userRepository',
    'commentRepository',
  ],
  sockets: {
    ticketData(msg: TicketWSMessageInterface): void {
      this.$emit('ticketUpdate', msg.ticket);
    },
    newTicketComment(msg: { ticketComment: TicketCommentInterface }): void {
      this.$emit('updateTicketComments', msg.ticketComment);
    },
    newTicketReplyComment(msg: { replyComment: TicketReplyCommentInterface }): void {
      this.$emit('updateTicketReplyComments', msg.replyComment);
    },
    updatedComment(msg: { comment: CommentInterface }): void {
      this.$emit('updatedCommentMessage', msg.comment);
    },
  },
})
export default class TicketFeedback extends Vue {
  private loading: boolean = true;
  private ticket!: TicketInterface;
  private feedback!: FeedbackInterface;
  private commentRepository!: CommentRepositoryInterface;
  private feedbackRepository!: FeedbackRepositoryInterface;
  private responseReceived: boolean = false;
  private ticketMessageReceived: boolean = false;
  private commentIdForDialog: number = 0;
  private deleteCommentDialog: string | false = false;
  private userHasScopes: object = UserHasScopes;
  private userRoles: object = Roles;
  private usersMap: { [userId: string]: UserBaseInfoInterface } = {};
  private replyComment: string = '';
  private comment: string = '';
  private userRepository!: UserRepoInterface;

  private noteIdForDialog: number = 0;
  private deleteNoteDialog: string | false = false;
  private commentIdForEditDialog: number = 0;
  private commentContentForDialog: string = '';
  private editCommentDialog: string | false = false;

  private ticketPriorities: Array<string | TicketPriorities>;

  public async created(): Promise<void> {
    await this.initialize();
  }

  public async beforeCreate(): Promise<void> {
    this.ticketPriorities = Object.values(TicketPriorities);
    this.$on('ticketUpdate', (ticket: TicketInterface) => {
      this.ticketMessageReceived = true;
      this.mergeTicketProperties(ticket);
    });

    this.$on('updateTicketComments', (ticketComment: TicketCommentInterface) => {

      if (ticketComment.ticketId !== this.ticket.id) {
        return;
      }

      this.ticket.comments.push(ticketComment);
      this.ticket.comments = this.ticket.comments.sort((a, b) => a.comment.timestamp - b.comment.timestamp);
      this.$forceUpdate();
    });

    this.$on('updateTicketReplyComments', (replyComment: TicketReplyCommentInterface) => {
      this.ticket.replies.forEach((reply) => {
        if (reply.id !== replyComment.replyId) {
          return;
        }

        reply.comments.push(replyComment);
        reply.comments = reply.comments.sort((a, b) => a.comment.timestamp - b.comment.timestamp);
      });

      this.$forceUpdate();
    });

    this.$on('updatedCommentMessage', (comment: CommentInterface) => {
      let commentIndex = this.ticket.comments.findIndex((ticketComment) => ticketComment.comment.id === comment.id);

      if (commentIndex !== -1) {
        this.ticket.comments[commentIndex].comment = comment;
      } else {
        let messageIndex = -1;
        this.ticket.replies.forEach((replay, mi) => {
          replay.comments.forEach((replyComment, ci) => {
            if (replyComment.comment.id === comment.id) {
              messageIndex = mi;
              commentIndex = ci;
            }
          });
        });

        if (messageIndex !== -1 && commentIndex !== -1) {
          this.ticket.replies[messageIndex].comments[commentIndex].comment = comment;
        }
      }

      this.$forceUpdate();
    });
  }


  private async initialize(): Promise<void> {
    const feedbackId = Number(this.$route.params.feedbackId);
    const feedbackResponse = await this.feedbackRepository.getOneById(feedbackId);
    this.feedback = feedbackResponse.feedback;
    this.ticket = feedbackResponse.ticket;
    this.responseReceived = true;
  }


  private async mergeTicketProperties(ticket: TicketInterface): Promise<void> {
    if (!this.responseReceived) {
      setTimeout(() => this.mergeTicketProperties(ticket), 100);
      return;
    }

    if (this.ticket.id !== ticket.id) {
      this.ticketMessageReceived = false;
      return;
    }

    const { agents, replies, ...omitted } = ticket;
    Object.assign(this.ticket, omitted);
    this.ticket.agents = ticket.agents;
    ticket.replies.forEach((reply) => {
      const index = this.ticket.replies.findIndex((rep) => rep.id === reply.id);
      if (index !== -1) {
        Object.assign(this.ticket.replies[index], reply);
      }
    });
    await this.getUsersInfo();
  }

  private async deleteComment(commentId: number): Promise<void> {
    this.commentIdForDialog = commentId;
    this.deleteCommentDialog = 'delete-comment-dialog';
  }

  private removeComment(commentId: number): void {
    let commentIndex = this.ticket.comments.findIndex((chatComment) => chatComment.comment.id === commentId);

    if (commentIndex !== -1) {
      delete this.ticket.comments[commentIndex];
      this.ticket.comments = this.ticket.comments.filter((comment) => !!comment);
    } else {
      let replyIndex = -1;

      this.ticket.replies.forEach((reply, ri) => {
        if (reply.comments) {
          reply.comments.forEach((messageComment, ci) => {
            if (messageComment.comment.id === commentId) {
              replyIndex = ri;
              commentIndex = ci;
            }
          });
        }
      });

      delete this.ticket.replies[replyIndex].comments[commentIndex];
      this.ticket.replies[replyIndex].comments = this.ticket.replies[replyIndex]
        .comments.filter((comment) => !!comment);
    }

    this.$forceUpdate();
  }

  private async addComment(): Promise<void> {
    try {
      const commentDTO: Partial<CommentInterface> = {
        content: this.comment,
        authorId: this.$store.getters.id,
        timestamp: Math.floor(Date.now() / 1000),
      };

      await this.commentRepository.addTicketComment(this.ticket.id, commentDTO, true);
      this.comment = '';

    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message);
    }
  }

  private async closeFeedback(): Promise<void> {
    if (this.feedback.status === FeedbackStatus.CLOSED) {
      return;
    }

    await this.feedbackRepository.changeFeedbackStatus(this.feedback.id, FeedbackStatus.CLOSED);
    this.feedback.status = FeedbackStatus.CLOSED;
    this.$forceUpdate();
  }

  private async getUsersInfo(): Promise<void> {
    const usersEmails: string[] = [];
    const usersIds: string[] = [];
    this.ticket.replies.forEach((reply) => {
      if (reply.name === this.ticket.name) {
        return;
      }

      if (usersEmails.indexOf(reply.email) === -1) {
        usersEmails.push(reply.email);
      }
      reply.comments.forEach((comment) => {
        if (usersIds.indexOf(comment.comment.authorId) === -1) {
          usersIds.push(comment.comment.authorId);
        }
      });
    });

    this.ticket.agents = this.ticket.agents.filter((agent) => !!agent.email);
    this.ticket.agents.forEach((agent) => {
      if (usersEmails.indexOf(agent.email) === -1) {
        usersEmails.push(agent.email);
      }
    });

    this.ticket.comments.forEach((comment) => {
      if (usersIds.indexOf(comment.comment.authorId) === -1) {
        usersIds.push(comment.comment.authorId);
      }
    });

    if (usersIds.indexOf(this.feedback.feedbackFromUserId) === -1) {
      usersIds.push(this.feedback.feedbackFromUserId);
    }

    if (usersIds.indexOf(this.feedback.feedbackToUserId) === -1) {
      usersIds.push(this.feedback.feedbackToUserId);
    }

    const users = [
      ...await this.userRepository.getUsersByEmails(usersEmails),
      ...await this.userRepository.getUsersByIds(usersIds),
    ];

    users.forEach((user) => {
      this.usersMap[user.id] = user;
      const agentIndex = this.ticket.agents.findIndex((agent) => agent.email === user.email);
      if (agentIndex !== -1) {
        this.ticket.agents[agentIndex].id = user.id;
      }
    });

    this.loading = false;
  }


  private async prepareCommentForEdit(commentId: number): Promise<void> {
    let commentIndex = this.ticket.comments.findIndex((ticketComment) => ticketComment.comment.id === commentId);
    if (commentIndex !== -1) {
      this.commentIdForEditDialog = this.ticket.comments[commentIndex].comment.id;
      this.commentContentForDialog = this.ticket.comments[commentIndex].comment.content;
      this.editCommentDialog = 'edit-comment-dialog';
    } else {
      let replyIndex = -1;

      this.ticket.replies.forEach((reply, mi) => {
        reply.comments.forEach((replyComment, ci) => {
          if (replyComment.comment.id === commentId) {
            replyIndex = mi;
            commentIndex = ci;
          }
        });
      });

      this.commentIdForEditDialog = this.ticket.replies[replyIndex].comments[commentIndex].comment.id;
      this.commentContentForDialog = this.ticket.replies[replyIndex].comments[commentIndex].comment.content;
      this.editCommentDialog = 'edit-comment-dialog';
    }
  }

  private async editComment(commentId: number, content: string): Promise<void> {
    let commentIndex = this.ticket.comments.findIndex((ticketComment) => ticketComment.comment.id === commentId);
    if (commentIndex !== -1) {
      this.ticket.comments[commentIndex].comment.content = content;
      this.ticket.comments[commentIndex].comment.updatedAt = Math.floor(Date.now() / 1000);
    } else {
      let replyIndex = -1;

      this.ticket.replies.forEach((reply, mi) => {
        reply.comments.forEach((replyComment, ci) => {
          if (replyComment.comment.id === commentId) {
            replyIndex = mi;
            commentIndex = ci;
          }
        });
      });

      this.ticket.replies[replyIndex].comments[commentIndex].comment.content = content;
      this.ticket.replies[replyIndex].comments[commentIndex].comment.updatedAt = Math.floor(Date.now() / 1000);
    }
  }
}
